/**
 * The pieces line of a review card, kept to one line until it is asked to open.
 *
 * A look review lists every garment in the look, which runs to four lines in a
 * narrow column and buries the review it belongs to. The line is clipped to one
 * line by CSS and a `<details>` opens it, so the toggle itself needs no script:
 * markup and CSS already do it, with keyboard and screen reader support built in.
 *
 * The one question CSS cannot answer is whether the text overflows at all, and
 * a caret on a line that already fits is a control that does nothing. So the
 * caret is drawn by default and this element takes it away, marking `data-fits`
 * on the lines that need no disclosure. Failing that way round means a customer
 * with no JavaScript keeps a working caret instead of a truncated line with no
 * way to read the rest.
 *
 * Measuring is width against width on the text itself, which is why the closed
 * state is `nowrap` rather than a line clamp: a clamp would have us compare
 * heights, and those disagree with themselves by a sub-pixel at some zooms.
 */

/** Slack in the overflow test, in px. Matches the carousel's own tolerance. */
const TOLERANCE = 1;

class ReviewPieces extends HTMLElement {
  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  connectedCallback() {
    // A carousel clone is an inert copy of a card, so its caret cannot be
    // clicked. Measuring one would only paint an affordance that is dead on
    // arrival; the stylesheet hides the caret there for the same reason.
    if (this.closest('[data-review-clone]')) return;

    this.#measure();

    this.#resizeObserver = new ResizeObserver(() => this.#measure());
    this.#resizeObserver.observe(this);

    // The web font is wider than the fallback, so a line that fits while the
    // fallback is on screen can need two lines once the real font swaps in.
    document.fonts?.ready.then(() => this.#measure());
  }

  disconnectedCallback() {
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
  }

  #measure = () => {
    const disclosure = this.querySelector('details');
    const text = this.querySelector('[data-pieces-text]');
    if (!disclosure || !text) return;

    // Open, the text wraps and overflows nothing, so the measurement would say
    // it fits and take away the caret the customer just used. Whatever was
    // decided while it was closed still holds.
    if (disclosure.open) return;

    const fits = text.scrollWidth - text.clientWidth <= TOLERANCE;

    this.toggleAttribute('data-fits', fits);

    // Without the caret the summary is plain text, and a tab stop that opens
    // nothing is a stop for nothing. A row carries up to twelve of these.
    const summary = disclosure.querySelector('summary');
    if (!summary) return;

    if (fits) {
      summary.setAttribute('tabindex', '-1');
    } else {
      summary.removeAttribute('tabindex');
    }
  };
}

if (!customElements.get('review-pieces')) {
  customElements.define('review-pieces', ReviewPieces);
}
